"use client";

import { useEffect, useRef } from "react";
import Link from "next/link";
import { trackQuoteRequest } from "@/lib/analytics";

const tiers = [
  {
    size: "Studio / 1 BR",
    price: "$125 – $150",
    duration: "2–3 hrs",
    popular: false,
    features: [
      "Full linen & towel replacement",
      "Toiletry restock",
      "Kitchen deep clean",
      "Bathroom sanitization",
      "Vacuum & mop all floors",
      "Trash removal",
      "Guest-ready staging",
      "47-point checklist",
    ],
  },
  {
    size: "2 Bedroom",
    price: "$175 – $200",
    duration: "3–4 hrs",
    popular: true,
    features: [
      "Everything in Studio/1BR",
      "Additional bedroom staging",
      "Baseboard cleaning",
      "Vent & fan cleaning",
      "Remote battery checks",
      "Appliance interior clean",
      "Patio/balcony wipe-down",
      "Photo documentation",
    ],
  },
  {
    size: "3 Bedroom",
    price: "$225 – $250",
    duration: "4–5 hrs",
    popular: false,
    features: [
      "Everything in 2BR",
      "Additional bathroom deep clean",
      "Window sill & track cleaning",
      "Refrigerator interior clean",
      "Oven interior clean",
      "Laundry coordination",
      "Supply inventory report",
      "Priority scheduling",
    ],
  },
  {
    size: "4+ Bedroom",
    price: "Custom Quote",
    duration: "5+ hrs",
    popular: false,
    features: [
      "Everything in 3BR",
      "Dedicated lead cleaner",
      "Multiple crew members",
      "Pool area cleaning",
      "Garage/storage tidy",
      "Outdoor furniture staging",
      "Same-day availability",
      "Dedicated account manager",
    ],
  },
];

const includes = [
  { icon: "🛏️", label: "Linen Replacement", desc: "Fresh linens and towels for every guest" },
  { icon: "🧴", label: "Toiletry Restock", desc: "Shampoo, conditioner, soap, paper goods" },
  { icon: "🍳", label: "Appliance Cleaning", desc: "Microwave, oven, fridge, dishwasher" },
  { icon: "🪟", label: "Baseboards & Vents", desc: "Dust-free surfaces guests notice" },
  { icon: "🔋", label: "Remote Battery Check", desc: "Never a dead remote for your guests" },
  { icon: "📸", label: "Guest-Ready Staging", desc: "Towel art, amenity display, first impressions" },
];

export default function CleaningPricing() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => { entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("revealed"); }); },
      { threshold: 0.08 }
    );
    const els = ref?.current?.querySelectorAll(".reveal-hidden");
    els?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="cleaning" ref={ref} className="section-padding bg-sand scroll-mt-20">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        {/* Header */}
        <div className="mb-14 reveal-hidden">
          <span className="text-gold text-xs font-bold uppercase tracking-widest block mb-3">Service A</span>
          <h2 className="font-heading text-4xl sm:text-5xl text-navy mb-5">
            Airbnb Turnover{" "}
            <span className="italic font-light text-gradient-gold">Cleaning</span>
          </h2>
          <p className="text-navy/60 text-lg max-w-2xl leading-relaxed">
            Competitive Panama City Beach rates. Every clean follows our 47-point checklist — the same standard used by PCB&apos;s top-rated Airbnbs.
          </p>
        </div>

        {/* Pricing cards */}
        <div id="pricing" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-16">
          {tiers?.map((tier, i) => (
            <div
              key={tier?.size}
              className={`reveal-hidden rounded-3xl overflow-hidden flex flex-col transition-all duration-300 ${
                tier?.popular
                  ? "pricing-popular text-white shadow-luxury-lg scale-105 ring-2 ring-gold/40"
                  : "bg-white border border-sand-medium card-luxury"
              }`}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              {tier?.popular && (
                <div className="bg-gold text-navy text-xs font-bold uppercase tracking-widest text-center py-2">
                  Most Requested
                </div>
              )}
              <div className="p-7 flex flex-col flex-1">
                <h3 className={`font-heading text-xl mb-1 ${tier?.popular ? "text-white" : "text-navy"}`}>
                  {tier?.size}
                </h3>
                <div className={`text-3xl font-heading font-bold mb-1 ${tier?.popular ? "text-gold" : "text-navy"}`}>
                  {tier?.price}
                </div>
                <p className={`text-xs mb-6 ${tier?.popular ? "text-white/60" : "text-navy/50"}`}>
                  per clean · est. {tier?.duration}
                </p>

                <ul className="flex flex-col gap-2.5 flex-1 mb-7">
                  {tier?.features?.map((f) => (
                    <li key={f} className={`flex items-start gap-2 text-sm ${tier?.popular ? "text-white/80" : "text-navy/70"}`}>
                      <svg className={`w-4 h-4 shrink-0 mt-0.5 ${tier?.popular ? "text-gold" : "text-gold"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {f}
                    </li>
                  ))}
                </ul>

                <Link
                  href="/contact"
                  onClick={() => trackQuoteRequest('cleaning', `services_pricing_${tier?.size?.toLowerCase()?.replace(/[^a-z0-9]/g, '_')}`)}
                  className={`w-full py-3.5 rounded-xl text-sm font-semibold text-center transition-all duration-300 ${
                    tier?.popular
                      ? "btn-gold" :"btn-navy"
                  }`}
                >
                  Book a Clean
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* What's included */}
        <div className="reveal-hidden">
          <h3 className="font-heading text-2xl text-navy mb-8 text-center">Every Clean Includes</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {includes?.map((item) => (
              <div key={item?.label} className="bg-white border border-sand-medium rounded-2xl p-5 text-center hover:border-gold/40 hover:shadow-md transition-all duration-300">
                <div className="text-3xl mb-3">{item?.icon}</div>
                <p className="font-semibold text-navy text-sm mb-1">{item?.label}</p>
                <p className="text-navy/50 text-xs leading-relaxed">{item?.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}